'use client';

import type { Dispatch, PropsWithChildren, SetStateAction } from 'react';
import { memo, useMemo, useState } from 'react';
import { noop } from 'lodash';
import { createContext, useContextSelector } from 'use-context-selector';
import type { FinderItem } from '@frameio/graphql-client';

type AssetsDnDContextValues = {
  draggingAsset: Partial<FinderItem> | undefined;
  setDraggingAsset: Dispatch<SetStateAction<Partial<FinderItem> | undefined>>;
  setVersionStackAnimating: Dispatch<SetStateAction<boolean>>;
  versionStackAnimating: boolean;
};

export const AssetsDnDContext = createContext<AssetsDnDContextValues>({
  draggingAsset: undefined,
  setDraggingAsset: noop,
  setVersionStackAnimating: noop,
  versionStackAnimating: false,
});

export const AssetsDnDContextProvider = memo(
  ({ children }: PropsWithChildren<Record<never, any>>) => {
    const [draggingAsset, setDraggingAsset] = useState<
      Partial<FinderItem> | undefined
    >(undefined);
    const [versionStackAnimating, setVersionStackAnimating] = useState(false);

    const context = useMemo(() => {
      return {
        draggingAsset,
        setDraggingAsset,
        setVersionStackAnimating,
        versionStackAnimating,
      };
    }, [
      draggingAsset,
      setDraggingAsset,
      versionStackAnimating,
      setVersionStackAnimating,
    ]);

    return (
      <AssetsDnDContext.Provider value={context}>
        {children}
      </AssetsDnDContext.Provider>
    );
  }
);

/**
 * Context for Drag & Drop animation state management.
 */
export const useAssetsDnDContext = () => {
  const draggingAsset = useContextSelector(
    AssetsDnDContext,
    (context) => context.draggingAsset
  );

  const setDraggingAsset = useContextSelector(
    AssetsDnDContext,
    (context) => context.setDraggingAsset
  );

  const versionStackAnimating = useContextSelector(
    AssetsDnDContext,
    (context) => context.versionStackAnimating
  );

  const setVersionStackAnimating = useContextSelector(
    AssetsDnDContext,
    (context) => context.setVersionStackAnimating
  );

  return {
    /**
     * The media info for the asset currently being dragged. Used by the version
     * stack animation to render the dragged asset in the hovered asset card.
     */
    draggingAsset,
    setDraggingAsset,

    /**
     * Whether or not there is currently a version stack creation animation
     * happening. Note that this is the animation when the user is actually
     * creating the version stack, not the initial animation on hover.
     */
    versionStackAnimating,
    setVersionStackAnimating,
  };
};

export const useSetVersionStackAnimating = () => {
  return useContextSelector(
    AssetsDnDContext,
    (context) => context.setVersionStackAnimating
  );
};
